import type {
  CeoBasisKind,
  CeoCadence,
  CeoDimensionKind,
  CeoMetricId,
  CeoPeriodKind,
} from './ceoCockpitTypes'

/** The four surfaces. Three rhythms and the diagnostic one. */
export type CeoSurface = CeoCadence | 'explore' | 'setup'
export type CeoExploreSection =
  | 'sales'
  | 'profit'
  | 'cash'
  | 'customers'
  | 'scorecard'
  | 'where'
  | 'people'
  | 'inventory'
  | 'subscriptions'
  | 'morning'

export const CEO_URL_PERIOD = 'fg_ceo_period'
export const CEO_URL_BASIS = 'fg_ceo_basis'
export const CEO_URL_LENS = 'fg_ceo_lens'
export const CEO_URL_METRIC = 'fg_ceo_metric'
export const CEO_URL_FOCUS = 'fg_ceo_focus'
export const CEO_URL_SURFACE = 'fg_ceo_surface'
export const CEO_URL_SECTION = 'fg_ceo_section'
export const CEO_URL_DATE = 'fg_ceo_date'

export type CeoCockpitUrlState = {
  surface: CeoSurface
  section: CeoExploreSection
  period: CeoPeriodKind
  basis: CeoBasisKind
  lens: CeoDimensionKind | null
  metric: CeoMetricId
  focus: string | null
  /** Exact retained snapshot date. Never restored from local storage. */
  date: string | null
}

const PERIODS: readonly CeoPeriodKind[] = ['mtd', 'qtd', 'ytd']
const BASES: readonly CeoBasisKind[] = ['plan', 'forecast', 'prior_year', 'prior_period']
const LENSES: readonly CeoDimensionKind[] = [
  'location',
  'class',
  'sales_rep',
  'employee',
  'customer_type',
  'channel',
  'item',
  'item_category',
  'customer',
]
const METRICS: readonly CeoMetricId[] = [
  'sales',
  'revenue',
  'gross_profit',
  'net_income',
  'cash',
  'accounts_receivable',
  'mrr',
]

const SURFACES: readonly CeoSurface[] = ['today', 'week', 'month', 'explore', 'setup']
const SECTIONS: readonly CeoExploreSection[] = [
  'sales',
  'profit',
  'cash',
  'customers',
  'scorecard',
  'where',
  'people',
  'inventory',
  'subscriptions',
  'morning',
]

const DEFAULT_STATE: CeoCockpitUrlState = {
  // A CEO opening the page is asking about this morning, not about the shape of
  // the quarter. Explore is where they go when they have a question.
  surface: 'today',
  section: 'sales',
  period: 'mtd',
  basis: 'plan',
  lens: null,
  metric: 'sales',
  focus: null,
  date: null,
}

const storageKey = (workbookId: string): string => `fg_ceo_cockpit:${workbookId}`

const isPeriod = (value: string): value is CeoPeriodKind =>
  (PERIODS as readonly string[]).includes(value)
const isBasis = (value: string): value is CeoBasisKind =>
  (BASES as readonly string[]).includes(value)
const isLens = (value: string): value is CeoDimensionKind =>
  (LENSES as readonly string[]).includes(value)
const isMetric = (value: string): value is CeoMetricId =>
  (METRICS as readonly string[]).includes(value)
const isSurface = (value: string): value is CeoSurface =>
  (SURFACES as readonly string[]).includes(value)
const isSection = (value: string): value is CeoExploreSection =>
  (SECTIONS as readonly string[]).includes(value)

function parseSurface(value: string | null): CeoSurface | null {
  const trimmed = trimOrNull(value)
  if (!trimmed) return null
  // `weekly`/`monthly` are the words the tabs use; accept both spellings so a
  // pasted link never lands on the wrong surface.
  const normalised = trimmed === 'weekly' ? 'week' : trimmed === 'monthly' ? 'month' : trimmed
  return isSurface(normalised) ? normalised : null
}

export function readCeoCockpitUrlState(
  loc: Pick<Location, 'search'> | null | undefined,
  workbookId?: string | null,
): CeoCockpitUrlState {
  const stored = workbookId ? readStoredState(workbookId) : null
  const params = safeParams(loc?.search)
  return {
    // Deliberately NOT restored from storage. A link may name a surface, but
    // opening the page fresh always lands on Today: a CEO who explored
    // yesterday must still get their morning brief tomorrow, or the ritual the
    // whole page is built around is broken by their own last click.
    surface: parseSurface(params.get(CEO_URL_SURFACE) || params.get('cadence')) || DEFAULT_STATE.surface,
    section: parseSection(params.get(CEO_URL_SECTION) || params.get('sec')) || stored?.section || DEFAULT_STATE.section,
    period: parsePeriod(params.get(CEO_URL_PERIOD) || params.get('p')) || stored?.period || DEFAULT_STATE.period,
    basis: parseBasis(params.get(CEO_URL_BASIS)) || stored?.basis || DEFAULT_STATE.basis,
    lens: parseLens(params.get(CEO_URL_LENS)) || stored?.lens || DEFAULT_STATE.lens,
    metric: parseMetric(params.get(CEO_URL_METRIC)) || stored?.metric || DEFAULT_STATE.metric,
    focus: trimOrNull(params.get(CEO_URL_FOCUS)) || stored?.focus || DEFAULT_STATE.focus,
    date: parseSnapshotDate(params.get(CEO_URL_DATE) || params.get('date')),
  }
}

export function writeCeoCockpitUrlState(
  next: CeoCockpitUrlState,
  workbookId?: string | null,
): void {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search || '')
  setOrDelete(params, CEO_URL_SURFACE, next.surface === DEFAULT_STATE.surface ? null : next.surface)
  setOrDelete(params, CEO_URL_SECTION, next.section === DEFAULT_STATE.section ? null : next.section)
  setOrDelete(params, CEO_URL_PERIOD, next.period === DEFAULT_STATE.period ? null : next.period)
  setOrDelete(params, CEO_URL_BASIS, next.basis === DEFAULT_STATE.basis ? null : next.basis)
  setOrDelete(params, CEO_URL_LENS, next.lens)
  setOrDelete(params, CEO_URL_METRIC, next.metric === DEFAULT_STATE.metric ? null : next.metric)
  setOrDelete(params, CEO_URL_FOCUS, next.focus)
  setOrDelete(params, CEO_URL_DATE, next.date)
  const search = params.toString()
  const url = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash || ''}`
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (url !== current) {
    window.history.replaceState(window.history.state, '', url)
  }
  if (workbookId) writeStoredState(workbookId, next)
}

export function ceoCockpitUrlStateEquals(a: CeoCockpitUrlState, b: CeoCockpitUrlState): boolean {
  return (
    a.surface === b.surface &&
    a.section === b.section &&
    a.period === b.period &&
    a.basis === b.basis &&
    a.lens === b.lens &&
    a.metric === b.metric &&
    a.focus === b.focus &&
    a.date === b.date
  )
}

function parsePeriod(value: string | null): CeoPeriodKind | null {
  const normalized = String(value || '').trim().toLowerCase()
  return isPeriod(normalized) ? normalized : null
}

function parseSection(value: string | null): CeoExploreSection | null {
  const normalized = String(value || '').trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'where_it_comes_from' || normalized === 'dimensions') return 'where'
  if (normalized === 'labor') return 'people'
  if (normalized === 'subs' || normalized === 'saas') return 'subscriptions'
  return isSection(normalized) ? normalized : null
}

function parseBasis(value: string | null): CeoBasisKind | null {
  const normalized = String(value || '').trim().toLowerCase().replace(/-/g, '_')
  return isBasis(normalized) ? normalized : null
}

function parseLens(value: string | null): CeoDimensionKind | null {
  const normalized = String(value || '').trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'product') return 'item'
  return isLens(normalized) ? normalized : null
}

function parseMetric(value: string | null): CeoMetricId | null {
  const normalized = String(value || '').trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'ar' || normalized === 'a_r') return 'accounts_receivable'
  if (normalized === 'gp') return 'gross_profit'
  if (normalized === 'ni') return 'net_income'
  return isMetric(normalized) ? normalized : null
}

function parseSnapshotDate(value: string | null): string | null {
  const normalized = String(value || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null
  const parsed = new Date(`${normalized}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== normalized) return null
  return normalized
}

function readStoredState(workbookId: string): CeoCockpitUrlState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(storageKey(workbookId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CeoCockpitUrlState>
    return {
      surface: DEFAULT_STATE.surface,
      section: parseSection(String(parsed.section || '')) || DEFAULT_STATE.section,
      period: parsePeriod(String(parsed.period || '')) || DEFAULT_STATE.period,
      basis: parseBasis(String(parsed.basis || '')) || DEFAULT_STATE.basis,
      lens: parseLens(parsed.lens == null ? null : String(parsed.lens)) || DEFAULT_STATE.lens,
      metric: parseMetric(String(parsed.metric || '')) || DEFAULT_STATE.metric,
      focus: trimOrNull(parsed.focus == null ? null : String(parsed.focus)),
      date: null,
    }
  } catch {
    return null
  }
}

function writeStoredState(workbookId: string, next: CeoCockpitUrlState): void {
  try {
    // Historical review is a link-scoped choice. Persisting it would make a
    // CEO reopen yesterday's books while believing they were seeing latest.
    window.localStorage.setItem(storageKey(workbookId), JSON.stringify({ ...next, date: null }))
  } catch {
    /* ignore quota / private mode */
  }
}

function safeParams(search: string | undefined): URLSearchParams {
  try {
    return new URLSearchParams(String(search || ''))
  } catch {
    return new URLSearchParams()
  }
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null | undefined): void {
  const trimmed = String(value || '').trim()
  if (!trimmed) params.delete(key)
  else params.set(key, trimmed)
}

function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = String(value || '').trim()
  return trimmed || null
}
